import { CornerType, GuideType } from '@/modules/ConfiguratorJoints/composables/useGuidesAndType';
import { JointShape } from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';
import {
    JointCanvasTemplateConfiguration,
    JointHalfAssetConfig,
} from '@/modules/ConfiguratorJoints/types/canvasTemplate';

export type ArmSide = 'left' | 'right';

export interface PerCornerStretchPixels {
    cornerSide: number;
    farSide: number;
}

export interface EndCapStretchFactors {
    positionOne: number;
    positionTwo: number;
}

export interface InnerExtraStretchOptions {
    armSide: ArmSide;
    cornerType: CornerType | null;
    longerArm: '1' | '2' | '';
    stretchFactors: EndCapStretchFactors;
}

export interface StretchedArmSlicesParams {
    imageAsset: JointHalfAssetConfig;
    stretchReferenceAsset?: JointHalfAssetConfig;
    image: HTMLImageElement;
    scale: number;
    factor: number;
    arm: ArmSide;
    reverseExtraFactor?: number;
    cornerExtraPixels?: number;
    farSideExtraPixels?: number;
}

const MM_TO_PX = 96 / 25.4;
export const CORNER_CAP_FRACTION = 0.35;
export const END_CAP_FRACTION = 0.3;
export const NON_REGULAR_GUIDE_INNER_STRETCH_PIXELS = 7;
export const ARM_STRETCH_DIRECTION: Record<ArmSide, -1 | 1> = {
    left: -1,
    right: 1,
};

export function isSelectedNonRegularGuide(guideType: GuideType | null): boolean {
    return guideType !== null && guideType !== 'alu-guide' && guideType !== 'concealed-guide';
}

export function getPerCornerGuideStretchPixels(
    cornerGuideType: GuideType | null,
    farGuideType: GuideType | null,
): PerCornerStretchPixels {
    return {
        cornerSide: isSelectedNonRegularGuide(cornerGuideType) ? NON_REGULAR_GUIDE_INNER_STRETCH_PIXELS : 0,
        farSide: isSelectedNonRegularGuide(farGuideType) ? NON_REGULAR_GUIDE_INNER_STRETCH_PIXELS : 0,
    };
}

export function getEndCapStretchFactors(selectedShape: JointShape | null): EndCapStretchFactors {
    if (selectedShape === 'internal') {
        return {
            positionOne: 0.25,
            positionTwo: 0.18,
        };
    }

    return {
        positionOne: 0.26,
        positionTwo: 0.3,
    };
}

export function getInnerExtraStretch({ armSide, cornerType, longerArm, stretchFactors }: InnerExtraStretchOptions): {
    factor: number;
    reversed: boolean;
} {
    const applies =
        cornerType === 'type-1' &&
        ((armSide === 'left' && longerArm === '1') || (armSide === 'right' && longerArm === '2'));
    const factor = armSide === 'left' ? stretchFactors.positionOne : stretchFactors.positionTwo;

    return { factor: applies ? factor : 0, reversed: armSide === 'left' };
}

export function applyArmStretchShift(
    localX: number,
    localY: number,
    side: -1 | 1,
    productSizeFactor: number,
    activeTemplate: JointCanvasTemplateConfiguration | null,
): { x: number; y: number } {
    if (!activeTemplate || productSizeFactor === 1) {
        return { x: localX, y: localY };
    }

    const armImage = activeTemplate.images[side === -1 ? 'left' : 'right'];
    const vertical = armImage.heightMm > armImage.widthMm;
    const armLengthMm = vertical ? armImage.heightMm : armImage.widthMm;
    const renderedLength = armLengthMm * activeTemplate.scale;
    const cornerCap = renderedLength * CORNER_CAP_FRACTION;
    const stretchDelta = (productSizeFactor - 1) * renderedLength;
    const axisValue = vertical ? localY : localX;
    let shifted = axisValue;

    if (side === -1 && axisValue < -cornerCap) shifted = axisValue - stretchDelta;
    if (side === 1 && axisValue > cornerCap) shifted = axisValue + stretchDelta;

    return vertical ? { x: localX, y: shifted } : { x: shifted, y: localY };
}

export function buildStretchedArmSlices({
    imageAsset,
    stretchReferenceAsset,
    image,
    scale,
    factor,
    arm,
    reverseExtraFactor = 0,
    cornerExtraPixels = 0,
    farSideExtraPixels = 0,
}: StretchedArmSlicesParams) {
    const renderedWidth = imageAsset.widthMm * scale;
    const renderedHeight = imageAsset.heightMm * scale;
    const sourceWidth = imageAsset.widthMm * MM_TO_PX;
    const sourceHeight = imageAsset.heightMm * MM_TO_PX;
    const vertical = imageAsset.heightMm > imageAsset.widthMm;
    const stretchAsset = stretchReferenceAsset ?? imageAsset;
    const stretchAssetIsVertical = stretchAsset.heightMm > stretchAsset.widthMm;
    const stretchReferenceLength = (stretchAssetIsVertical ? stretchAsset.heightMm : stretchAsset.widthMm) * scale;
    const fixedPixelStretch = cornerExtraPixels + farSideExtraPixels;

    const stretchAxisDirection = arm === 'left' ? -1 : 1;
    const reverseStretchLength = reverseExtraFactor * stretchReferenceLength + cornerExtraPixels;
    const reverseOffset = -stretchAxisDirection * reverseStretchLength;
    const applyReverse = <T extends { x: number; y: number }>(slices: T[]): T[] =>
        reverseStretchLength === 0
            ? slices
            : slices.map((slice) =>
                  vertical ? { ...slice, y: slice.y + reverseOffset } : { ...slice, x: slice.x + reverseOffset },
              );

    if (vertical) {
        const cornerCapHeight = renderedHeight * CORNER_CAP_FRACTION;
        const endCapHeight = renderedHeight * END_CAP_FRACTION;
        const stretchedArmHeight =
            renderedHeight + (factor - 1) * stretchReferenceLength + fixedPixelStretch - cornerCapHeight - endCapHeight;
        const cropCornerCapHeight = sourceHeight * CORNER_CAP_FRACTION;
        const cropArmHeight = sourceHeight * (1 - CORNER_CAP_FRACTION - END_CAP_FRACTION);
        const cropEndCapHeight = sourceHeight * END_CAP_FRACTION;

        if (arm === 'left') {
            const leftX = -renderedWidth;
            const cornerCapTop = -cornerCapHeight;
            const armTop = cornerCapTop - stretchedArmHeight;
            const endCapTop = armTop - endCapHeight;
            return applyReverse([
                {
                    image,
                    x: leftX,
                    y: endCapTop,
                    width: renderedWidth,
                    height: endCapHeight,
                    crop: { x: 0, y: 0, width: sourceWidth, height: cropEndCapHeight },
                },
                {
                    image,
                    x: leftX,
                    y: armTop,
                    width: renderedWidth,
                    height: stretchedArmHeight,
                    crop: { x: 0, y: cropEndCapHeight, width: sourceWidth, height: cropArmHeight },
                },
                {
                    image,
                    x: leftX,
                    y: cornerCapTop,
                    width: renderedWidth,
                    height: cornerCapHeight,
                    crop: {
                        x: 0,
                        y: sourceHeight - cropCornerCapHeight,
                        width: sourceWidth,
                        height: cropCornerCapHeight,
                    },
                },
            ]);
        }

        const armTop = cornerCapHeight;
        const endCapTop = armTop + stretchedArmHeight;
        return applyReverse([
            {
                image,
                x: 0,
                y: 0,
                width: renderedWidth,
                height: cornerCapHeight,
                crop: { x: 0, y: 0, width: sourceWidth, height: cropCornerCapHeight },
            },
            {
                image,
                x: 0,
                y: armTop,
                width: renderedWidth,
                height: stretchedArmHeight,
                crop: { x: 0, y: cropCornerCapHeight, width: sourceWidth, height: cropArmHeight },
            },
            {
                image,
                x: 0,
                y: endCapTop,
                width: renderedWidth,
                height: endCapHeight,
                crop: { x: 0, y: sourceHeight - cropEndCapHeight, width: sourceWidth, height: cropEndCapHeight },
            },
        ]);
    }

    const cornerCapWidth = renderedWidth * CORNER_CAP_FRACTION;
    const endCapWidth = renderedWidth * END_CAP_FRACTION;
    const stretchedArmWidth =
        renderedWidth + (factor - 1) * stretchReferenceLength + fixedPixelStretch - cornerCapWidth - endCapWidth;
    const cropEndCapWidth = sourceWidth * END_CAP_FRACTION;
    const cropArmWidth = sourceWidth * (1 - END_CAP_FRACTION - CORNER_CAP_FRACTION);
    const cropCornerCapWidth = sourceWidth * CORNER_CAP_FRACTION;

    if (arm === 'left') {
        const top = -renderedHeight;
        const cornerCapLeft = -cornerCapWidth;
        const armLeft = cornerCapLeft - stretchedArmWidth;
        const endCapLeft = armLeft - endCapWidth;
        return applyReverse([
            {
                image,
                x: endCapLeft,
                y: top,
                width: endCapWidth,
                height: renderedHeight,
                crop: { x: 0, y: 0, width: cropEndCapWidth, height: sourceHeight },
            },
            {
                image,
                x: armLeft,
                y: top,
                width: stretchedArmWidth,
                height: renderedHeight,
                crop: { x: cropEndCapWidth, y: 0, width: cropArmWidth, height: sourceHeight },
            },
            {
                image,
                x: cornerCapLeft,
                y: top,
                width: cornerCapWidth,
                height: renderedHeight,
                crop: { x: sourceWidth - cropCornerCapWidth, y: 0, width: cropCornerCapWidth, height: sourceHeight },
            },
        ]);
    }

    const armLeft = cornerCapWidth;
    const endCapLeft = armLeft + stretchedArmWidth;
    return applyReverse([
        {
            image,
            x: 0,
            y: 0,
            width: cornerCapWidth,
            height: renderedHeight,
            crop: { x: 0, y: 0, width: cropCornerCapWidth, height: sourceHeight },
        },
        {
            image,
            x: armLeft,
            y: 0,
            width: stretchedArmWidth,
            height: renderedHeight,
            crop: { x: cropCornerCapWidth, y: 0, width: cropArmWidth, height: sourceHeight },
        },
        {
            image,
            x: endCapLeft,
            y: 0,
            width: endCapWidth,
            height: renderedHeight,
            crop: { x: sourceWidth - cropEndCapWidth, y: 0, width: cropEndCapWidth, height: sourceHeight },
        },
    ]);
}

export function getNearestEdgeDelta(
    guidePosition: number,
    baseMin: number,
    baseMax: number,
    stretchedMin: number,
    stretchedMax: number,
): number {
    return Math.abs(guidePosition - baseMin) < Math.abs(guidePosition - baseMax)
        ? stretchedMin - baseMin
        : stretchedMax - baseMax;
}
